import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "../icons.jsx";
import { api, ApiError, dateTime } from "../api.js";
import { ACTIVE, shortName } from "./common.jsx";
import { PERIODS, DEFAULT_PERIOD, periodRange } from "./period.js";
import KeyLogin from "../KeyLogin.jsx";
import Overview from "./Overview.jsx";
import Erpn from "./Erpn.jsx";
import Card from "./Card.jsx";
import Budget from "./Budget.jsx";
import Sea from "./Sea.jsx";
import Smkor from "./Smkor.jsx";
import Reports from "./Reports.jsx";
import Partners from "./Partners.jsx";
import Movement from "./Movement.jsx";
import Customs from "./Customs.jsx";
import Banks from "./banks/Banks.jsx";
import RefParams from "./RefParams.jsx";
import Reference from "./Reference.jsx";
import Compliance from "./Compliance.jsx";
import Correspondence from './post/Correspondence.jsx';
import Requests from './requests/Requests.jsx';
import OppSection from './opp/OppSection.jsx';
import PnNew from "./pn/PnNew.jsx";
import PnWizard from "./pn/PnWizard.jsx";
import Primary from "./primary/Primary.jsx";
import Prro from "./Prro.jsx";
import Inventory from "./Inventory.jsx";
import Pos from "./Pos.jsx";

// Розділи компанії, згруповані для бічного меню
export const NAV = [
  { title: null, items: [{ key: "overview", title: "Огляд", icon: "home" }, { key: "banks", title: "Банки", icon: "wallet" }] },
  { title: "Кабінет ДПС", items: [
    { key: "erpn", title: "ПН/РК", icon: "invoice" },
    { key: "primary", title: "Первинні документи", icon: "folder" },
    { key: "reports", title: "Звітність", icon: "submit" },
    { key: "requests", title: "Запити й заяви", icon: "request" },
    { key: "correspondence", title: "Листування з ДПС", icon: "mail" },
    { key: "opp", title: "Об’єкти · 20-ОПП", icon: "pin" },
    { key: "budget", title: "Розрахунки", icon: "budget" },
    { key: "sea", title: "СЕА", icon: "sea" },
    { key: "smkor", title: "СМКОР", icon: "smkor" },
    { key: "prro", title: "Каси й продажі", icon: "budget" },
    { key: "pos", title: "Каса", icon: "wallet" },
  ] },
  { title: "Аналітика", items: [
    { key: "partners", title: "Контрагенти", icon: "partners" },
    { key: "movement", title: "Рух товарів і послуг", icon: "movement" },
    { key: "inventory", title: "Склад", icon: "folder" },
    { key: "customs", title: "Митні декларації", icon: "customs" },
    { key: "compliance", title: "Комплаєнс", icon: "compliance" },
  ] },
  // Довідники й дані компанії — без заголовка групи, щоб меню вміщалося на стандартному екрані
  { items: [{ key: "reference", title: "УКТЗЕД / ДКПП", icon: "reference" }, { key: "params", title: "Показники", icon: "sliders" }, { key: "card", title: "Дані компанії", icon: "company" }] },
];
export const SECTIONS = NAV.flatMap((g) => g.items);
// Створення ПН: #/c/<id>/pn — вибір сценарію й чернетки, #/c/<id>/pn/<чернетка> — майстер
const PnSection = (p) => (/^[0-9a-f-]{36}$/.test(p.sub || "") ? <PnWizard key={p.sub} {...p} draftId={p.sub} /> : <PnNew {...p} />);
const VIEWS = { requests: Requests, correspondence: Correspondence, opp: OppSection, overview: Overview, erpn: Erpn, pn: PnSection, primary: Primary, compliance: Compliance, partners: Partners, movement: Movement, customs: Customs, banks: Banks, reference: Reference, params: RefParams,
  card: Card, budget: Budget, sea: Sea, smkor: Smkor, reports: Reports, prro: Prro, inventory: Inventory, pos: Pos };
export const roleName = { viewer: "перегляд", preparer: "підготовка", submitter: "подання", owner: "власник" };

const readPeriod = () => { try { return localStorage.getItem("etax.period") || DEFAULT_PERIOD; } catch { return DEFAULT_PERIOD; } };
const savePeriod = (k) => { try { localStorage.setItem("etax.period", k); } catch {} };

// Стан сесії ДПС: діє, можна спробувати продовжити (refresh_token — не гарантія), потрібен вхід
export function dpsState(s) {
  if (!s) return null;
  if (s.accessValid) return { tone: "ok", text: "ДПС підключено" };
  if (s.refreshAvailable) return { tone: "ok", text: "ДПС: можна спробувати продовжити сесію" };
  return { tone: "bad", text: "ДПС: потрібен вхід ключем" };
}

export default function Company({ id, section, sub, navOpen, onNavClose }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [relogin, setRelogin] = useState(false);
  const [period, setPeriod] = useState(readPeriod);
  // Лічильник версій даних розділу: росте, коли його задача щойно завершилась
  const [versions, setVersions] = useState({});
  const prevJobs = useRef({});
  const reloginRef = useRef(null);
  // Escape закриває висувне меню розділів
  useEffect(() => {
    if (!navOpen) return;
    const on = (e) => { if (e.key === "Escape") onNavClose(); };
    addEventListener("keydown", on);
    return () => removeEventListener("keydown", on);
  }, [navOpen, onNavClose]);

  const loadInfo = useCallback(() => api(`/api/companies/${id}`).then(setInfo).catch(setError), [id]);
  const loadOverview = useCallback(async () => {
    try {
      const o = await api(`/api/companies/${id}/overview`);
      const bumped = {};
      for (const r of o.resources) {
        const prev = prevJobs.current[r.key];
        const now = r.lastJob ? `${r.lastJob.id}:${r.lastJob.status}` : "";
        if (prev !== undefined && prev !== now && r.lastJob && !ACTIVE.includes(r.lastJob.status)) bumped[r.key] = true;
        prevJobs.current[r.key] = now;
      }
      if (Object.keys(bumped).length) {
        setVersions((v) => {
          const n = { ...v, all: (v.all || 0) + 1 };
          for (const k of Object.keys(bumped)) n[k] = (v[k] || 0) + 1;
          return n;
        });
        if (o.resources.some((r) => bumped[r.key] && r.lastJob.status === "needs_login")) loadInfo();
      }
      setOverview(o);
      setOverviewError(null);
      return o;
    } catch (e) {
      setOverviewError(e);
      return null;
    }
  }, [id, loadInfo]);

  useEffect(() => {
    prevJobs.current = {}; setOverview(null); setInfo(null); setError(null);
    loadInfo(); loadOverview();
  }, [loadInfo, loadOverview]);

  // Поки якийсь розділ у черзі чи оновлюється — опитуємо стан
  const busy = Boolean(overview?.resources.some((r) => ACTIVE.includes(r.lastJob?.status)));
  useEffect(() => {
    if (!busy) return;
    const t = setTimeout(loadOverview, 1500);
    return () => clearTimeout(t);
  }, [busy, overview, loadOverview]);

  const sync = async (resources) => {
    await api(`/api/companies/${id}/sync`, { method: "POST", body: resources ? { resources } : {} });
    await loadOverview();
  };
  const openRelogin = () => {
    setRelogin(true);
    setTimeout(() => window.scrollTo({ top: 0 }), 0);
  };

  if (error) return <div className="cab-main"><p className="error">{error instanceof ApiError && error.status === 404 ? "Компанію не знайдено" : error.message}</p></div>;
  if (!info) return <div className="cab-main"><p className="muted" role="status">Завантаження…</p></div>;
  const { company, dpsSession } = info;
  const ds = dpsState(dpsSession);
  const canSync = dpsSession.accessValid || dpsSession.refreshAvailable;
  const current = VIEWS[section] ? section : "overview";
  const View = VIEWS[current];
  const res = Object.fromEntries((overview?.resources || []).map((r) => [r.key, r]));
  const lastSync = (overview?.resources || []).map((r) => r.lastSuccessAt).filter(Boolean).sort().at(-1);
  const range = periodRange(period);

  // Створення ПН — частина розділу ПН/РК; каси мають власний ресурс оновлення.
  const DATA_OF = { pn: "erpn" };
  const navKey = DATA_OF[current] || current;
  // Кнопка в шапці оновлює поточний розділ (якщо в нього є власні дані ДПС), поруч — «усі розділи»
  const sectionRes = navKey !== "overview" ? res[navKey] : null;
  const sectionTitle = SECTIONS.find((x) => x.key === navKey)?.title || "";
  const failed = (k) => res[k] && ["failed", "needs_login"].includes(res[k].lastJob?.status);
  // Податковий профіль: розділи, що не стосуються компанії, не показуємо в меню; без підтвердженого профілю — усе (сумісність)
  const caps = info.capabilities;
  const off = (k) => Boolean(caps && !caps.compat && caps.sections?.[k]?.applicable === false);

  return (
    <div className={`cab${navOpen ? " nav-open" : ""}`}>
      <nav id="cab-nav" className="cab-nav" aria-label="Розділи компанії">
        {NAV.map((g) => ({ ...g, items: g.items.filter((s) => !off(s.key)) })).filter((g) => g.items.length).map((g, gi) => (
          <div key={gi} className="cab-nav-group">
            {g.title && <p className="cab-nav-title" id={`nav-g${gi}`}>{g.title}</p>}
            <ul aria-labelledby={g.title ? `nav-g${gi}` : undefined}>
              {g.items.map((s) => (
                <li key={s.key}>
                  <a href={`#/c/${id}/${s.key}`} className={s.key === navKey ? "active" : ""} aria-current={s.key === navKey ? "page" : undefined}>
                    <Icon name={s.icon} /><span>{s.title}</span>
                    {failed(s.key) && <span className="nav-alert" role="img" aria-label="помилка оновлення" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {navOpen && <div className="cab-scrim" onClick={onNavClose} aria-hidden="true" />}
    <section className="cab-main" aria-labelledby="company-title">
      {/* Потрібен повторний вхід ключем — у робочій області лише форма входу, по центру; розділи — після входу */}
      {relogin ? (
        <div className="relogin-only" ref={reloginRef}>
          <h1 id="company-title" className="sr-only">{company.name || company.edrpou}: вхід ключем КЕП</h1>
          <KeyLogin expectEdrpou={company.edrpou} autoFocus onDone={() => { setRelogin(false); loadInfo(); loadOverview(); }} />
          <p className="relogin-cancel"><button type="button" className="linkish" onClick={() => setRelogin(false)}>Повернутися без входу</button></p>
        </div>
      ) : (<>
      <header className="co-head">
        <div className="co-id">
          <h1 id="company-title">{shortName(company.name) || company.edrpou}</h1>
          <details className="co-details">
            <summary>ЄДРПОУ {company.edrpou} · {roleName[company.role]}</summary>
            <p>{company.name}</p>
          </details>
        </div>
        <div className="co-meta">
          {current === "overview" && (
            <label className="co-period">
              <span className="sr-only">Період</span>
              <select value={period} onChange={(e) => { setPeriod(e.target.value); savePeriod(e.target.value); }}>
                {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
          )}
          <span className={`chip chip-${ds.tone}`}><span className="status-dot" aria-hidden="true" />{ds.text}</span>
          <span className="co-sync muted" aria-live="polite">
            {busy ? "Синхронізація…" : lastSync ? `Синхронізовано ${dateTime(lastSync)}` : "Ще не синхронізовано"}
          </span>
          {canSync ? (sectionRes ? (
            <span className="btn-split" role="group" aria-label="Оновлення даних з ДПС">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => sync()} disabled={busy}>Оновити усі розділи</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => sync([navKey])} disabled={busy}>
                {ACTIVE.includes(sectionRes.lastJob?.status) ? "Оновлюється…" : `Оновити ${sectionTitle}`}</button>
            </span>
          ) : <button type="button" className="btn btn-secondary btn-sm" onClick={() => sync()} disabled={busy}>{busy ? "Оновлюється…" : "Синхронізувати все"}</button>)
            : <button type="button" className="btn btn-sm" onClick={openRelogin}>Увійти ключем</button>}
        </div>
      </header>

      {caps?.compat && current === "overview" && (
        <section className="card attention-inline tp-ask" role="status">
          <b>Підтвердіть податковий профіль компанії</b> — від нього залежать розділи, синхронізація з ДПС і звітність.
          Дані з картки ДПС уже підставлено, потрібне лише підтвердження. <a href={`#/c/${id}/card`}>Перейти до «Дані компанії» →</a>
        </section>
      )}
      {off(current) ? (
        <section className="card" role="status">
          <h2>{SECTIONS.find((s) => s.key === current)?.title}</h2>
          <p>Розділ не стосується вашого податкового профілю: {caps.sections[current].reason}.</p>
          <p className="muted small">Профіль: {info.taxProfile?.text}. Змінити — у <a href={`#/c/${id}/card`}>«Дані компанії»</a>.</p>
        </section>
      ) : (
      <View companyId={id} company={company} sub={sub} dpsSession={dpsSession} overview={overview} overviewError={overviewError}
        res={res[current]} resources={res} version={versions[current] || 0} versions={versions}
        period={range} busy={busy} canSync={canSync} capabilities={caps} taxProfile={info.taxProfile} onProfileChange={loadInfo}
        onSync={() => sync([current])} onSyncAll={() => sync()} onRelogin={openRelogin} />
      )}
      </>)}
    </section>
    </div>
  );
}
